'use client';

import { type ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@/components/link';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { USER_ACTION_ITEM_CLASSES } from './item-classes';

// TODO(SNKSN-152): replace with the real "Events & treatments" route once it exists.
const EVENTS_AND_TREATMENTS_HREF = '/events-and-treatments';

const linkClasses = 'block py-1 text-sm text-popover-foreground hover:opacity-50 transition-opacity';

/**
 * STORES dropdown: a text trigger opening a panel of store links ("Find a store" → SFRA store locator,
 * "Events & treatments"). Hover-opens with a short close delay; keyboard/click via the Radix popover.
 * Visibility is the consumer's call via `className` (the header hides it on mobile).
 */
export const StoresMenu = ({ className }: { className?: string }): ReactElement => {
    const { t } = useTranslation('header');
    const [open, setOpen] = useState(false);
    const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (closeTimeout.current) {
                clearTimeout(closeTimeout.current);
            }
        },
        []
    );

    const openNow = () => {
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current);
            closeTimeout.current = null;
        }
        setOpen(true);
    };

    const closeSoon = () => {
        closeTimeout.current = setTimeout(() => setOpen(false), 150);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={cn(USER_ACTION_ITEM_CLASSES, className)}
                    onMouseEnter={openNow}
                    onMouseLeave={closeSoon}>
                    {t('userActions.stores')}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-max min-w-44" onMouseEnter={openNow} onMouseLeave={closeSoon}>
                <p className="mb-3 text-sm font-medium uppercase">{t('userActions.stores')}</p>
                <nav className="flex flex-col gap-2">
                    {/* "/stores" is the SFRA store-locator (Stores controller). */}
                    <Link to="/stores" className={linkClasses}>
                        {t('userActions.findAStore')}
                    </Link>
                    <Link to={EVENTS_AND_TREATMENTS_HREF} className={linkClasses}>
                        {t('userActions.eventsAndTreatments')}
                    </Link>
                </nav>
            </PopoverContent>
        </Popover>
    );
};
